/*
 * Copyright 2026 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 */
package studio

import kotlinx.datetime.LocalDate
import kotlinx.serialization.Serializable
import models.GoalAccountType
import models.GoalCategory

/**
 * Goal Allocation Studio data models — see docs/Goal Allocation Studio Spec.
 *
 * Reuses [GoalAccountType] (from `shared/src/.../models/GoalAccountType.kt`) as
 * the vehicle enum so a goal's `accountType` field doubles as the Studio's
 * `chosenVehicle` — no parallel taxonomy.
 */

typealias VehicleType = GoalAccountType

/**
 * Region gates the Studio between Canadian (tax-aware) and Generic modes.
 * See §16 in the spec.
 */
@Serializable
enum class Region { CA, US, OTHER }

/**
 * Canadian provinces + territories. Ignored when [UserTaxContext.region] != CA.
 */
@Serializable
enum class ProvinceCode { AB, BC, MB, NB, NL, NS, NT, NU, ON, PE, QC, SK, YT }

/**
 * Drives every Studio computation. See §3.1 + §16.2.
 */
@Serializable
data class UserTaxContext(
    val region: Region,
    val province: ProvinceCode,
    val grossAnnualIncome: Double,
    val isSelfEmployed: Boolean = false,
    val taxYear: Int,
    val marginalRateNow: Double,
    val marginalRateAtWithdrawal: Double? = null,
    val isFirstTimeHomeBuyer: Boolean = true,
    val hasDependentChildren: Boolean = false,
    val hasDisabilityTaxCredit: Boolean = false,
)

/** Remaining room per vehicle. See §3.3. */
@Serializable
data class ContributionRoom(
    val tfsa: Double,
    val rrsp: Double,
    val fhsa: FhsaRoom,
    val resp: RespRoom = RespRoom(remaining = 0.0, cesgRemaining = 0.0),
    val rdsp: RdspRoom = RdspRoom(remaining = 0.0),
    /** True when the user said they don't know their current TFSA room
     *  — in that case the engine refuses to assume future accrual either. */
    val tfsaUnknown: Boolean = false,
    val rrspUnknown: Boolean = false,
)

@Serializable
data class FhsaRoom(
    val remaining: Double,
    val lifetimeCap: Double = 40_000.0,
    val yearsUntilExpiry: Int = 15,
)

@Serializable
data class RespRoom(val remaining: Double, val cesgRemaining: Double)

@Serializable
data class RdspRoom(val remaining: Double)

/**
 * Recommendation badge for a vehicle relative to the others. See §3.4.
 * Math-derived; the "priority match" sub-badge from §14 question 7 is a
 * separate UI concept and doesn't change [Recommendation].
 */
@Serializable
enum class Recommendation { BEST_FIT, BACKUP, NOT_IDEAL, INELIGIBLE }

/** Calendar position + state. See §3.4. */
@Serializable
data class VehiclePoint(
    val date: LocalDate,
    val grossBalance: Double,
    val totalWealth: Double,
    val netToUser: Double,
    /** Contribution room left in this vehicle after the contribution this
     *  month is applied. 0 for vehicles without a room concept (HISA,
     *  NON_REGISTERED). Drives the room-schedule mini-chart so the user
     *  sees the sawtooth: drains as they contribute, jumps up at year
     *  boundaries when new room accrues. */
    val roomRemaining: Double = 0.0,
    /** What was actually contributed this month — may be less than the
     *  slider's requested value if room ran out. */
    val monthlyContribution: Double = 0.0,
)

@Serializable
enum class VehicleEventType { REFUND, GRANT, TAX_OWED, EXPIRY_WARNING, MILESTONE }

/** Tax event marked on a chart. See §3.4 + §5.2. */
@Serializable
data class VehicleEvent(
    val date: LocalDate,
    val type: VehicleEventType,
    val amount: Double,
    val label: String,
    val explanation: String,
)

/** Engine output per vehicle. See §3.4. */
@Serializable
data class VehicleProjection(
    val vehicle: VehicleType,
    val eligible: Boolean,
    val eligibilityReason: String? = null,
    val recommendation: Recommendation,
    val recommendationReason: String,
    val points: List<VehiclePoint>,
    val events: List<VehicleEvent>,
    val finalNetToUser: Double,
    val effectiveSavingsRateContribution: SavingsRateBreakdown,
)

// ---------------------------------------------------------------------------
// Debt Paydown Studio — sibling tool to the Allocation Studio for goals in
// the DEBT_PAYOFF category. Compares paydown strategies (current vs an
// adjusted plan with different rate / payment / lump sum) by simulating
// month-by-month amortization. Phase 1 ships fixed-rate amortization for
// any debt type; variable-rate projections + mortgage term/amortization
// split are Phase 2.
// ---------------------------------------------------------------------------

/**
 * How often a debt payment is made. Drives both the projection cadence and
 * the Canadian "accelerated bi-weekly" hidden-extra-payment behavior.
 *
 * - MONTHLY                : 12 periods/yr; period payment = M; total = 12 × M
 * - SEMI_MONTHLY           : 24 periods/yr; period payment = M/2; total = 12 × M
 *                            (same total annual cash flow as monthly; tiny
 *                            interest savings from earlier principal hits)
 * - ACCELERATED_BIWEEKLY   : 26 periods/yr; period payment = M/2; total = 13 × M
 *                            (Canadian quirk: paying half-the-monthly every two
 *                            weeks hides ONE extra monthly payment per year —
 *                            the real reason this option saves interest)
 *
 * Plain bi-weekly (26/yr at M × 12/26) is intentionally NOT modelled — its
 * outcome is essentially identical to MONTHLY and showing it would obscure
 * why ACCELERATED_BIWEEKLY is the win.
 */
@Serializable
enum class PaymentFrequency {
    MONTHLY,
    SEMI_MONTHLY,
    ACCELERATED_BIWEEKLY,
    ;

    fun periodsPerYear(): Int = when (this) {
        MONTHLY -> 12
        SEMI_MONTHLY -> 24
        ACCELERATED_BIWEEKLY -> 26
    }

    /** Convert a monthly-equivalent payment M to the dollar amount actually
     *  written per period under this frequency. */
    fun periodPayment(monthlyPayment: Double): Double = when (this) {
        MONTHLY -> monthlyPayment
        SEMI_MONTHLY -> monthlyPayment / 2.0
        ACCELERATED_BIWEEKLY -> monthlyPayment / 2.0
    }
}

@Serializable
enum class DebtScenarioKind {
    /** First editable plan (left-hand card). Cyan accent. */
    PLAN_A,
    /** Second editable plan (right-hand card). Amber accent. */
    PLAN_B,
}

@Serializable
data class DebtScenario(
    val kind: DebtScenarioKind,
    val initialBalance: Double,
    /** Annual interest rate as a fraction (e.g. 0.0385 for 3.85%). */
    val annualRate: Double,
    /** Monthly-equivalent payment. The projector converts to a per-period
     *  amount via [PaymentFrequency.periodPayment]. */
    val monthlyPayment: Double,
    /** Extra principal applied immediately at month 0. */
    val lumpSum: Double = 0.0,
    /** Payment cadence. Drives both the projection cadence and the
     *  per-period payment amount. */
    val paymentFrequency: PaymentFrequency = PaymentFrequency.MONTHLY,
)

@Serializable
data class DebtPoint(
    val month: Int,
    val balance: Double,
)

@Serializable
data class DebtProjection(
    val scenario: DebtScenario,
    val points: List<DebtPoint>,
    val monthsToPayoff: Int,
    val totalInterestPaid: Double,
    /** Principal + interest. Total cash out of pocket. */
    val totalCost: Double,
    /** True when the scheduled payment doesn't fully cover monthly
     *  interest — credit-card-style negative amortization. We surface this
     *  to the user as a warning since the engine treats it as "never
     *  paid off" in that case. */
    val negativeAmortization: Boolean = false,
)

/** §3.4 / §4.3. Two components: dollars actually contributed vs dollars sheltered by tax. */
@Serializable
data class SavingsRateBreakdown(
    val outOfPocket: Double,
    val taxShifted: Double,
    val total: Double,
)

// ---------------------------------------------------------------------------
// Profile (Onboarding & Profile — §14)
// ---------------------------------------------------------------------------

@Serializable
enum class AgeBand { EIGHTEEN_TO_TWENTYNINE, THIRTY_TO_THIRTYNINE, FORTY_TO_FORTYNINE, FIFTY_TO_FIFTYNINE, SIXTY_TO_SIXTYNINE, SEVENTY_PLUS }

@Serializable
enum class HomeOwnership { NEVER_OWNED, OWNED_BEFORE, DECLINED }

@Serializable
enum class RoomKnowledge { KNOWN, ROUGH, UNKNOWN }

@Serializable
enum class SavingsTenure { NEVER, FEW_MONTHS, ONE_TO_THREE_YEARS, FIVE_PLUS }

@Serializable
enum class IncomeInputMode { GROSS_ANNUAL, AVERAGE_PAYCHEQUE, BANK_PRE_FILL }

@Serializable
enum class PayFrequency { MONTHLY, BI_WEEKLY, ANNUAL }

/**
 * Disclosed employer / external inflow into a registered account. See §15.3.
 * Multiple sources allowed; the math engine annualizes each and subtracts
 * against the right vehicle's room.
 */
@Serializable
enum class InflowSourceType { GROUP_RRSP, DPSP, DB_PENSION, STOCK_PURCHASE_RRSP, STOCK_PURCHASE_TFSA }

@Serializable
data class InflowSource(
    val id: String,
    val type: InflowSourceType,
    val amountPerPeriod: Double,
    val frequency: PayFrequency,
    val employerMatchPercent: Double? = null,   // GROUP_RRSP only
    val annualPensionAdjustment: Double? = null, // DPSP / DB_PENSION
    val destinationVehicle: VehicleType,
    val startedYear: Int,
    val active: Boolean = true,
)

/**
 * Persistent user profile JSON. Stored in `user_settings` under the key
 * `wiz3r_user_profile_json` (per §14.5). Reusing the existing key/value
 * store avoids a schema migration; the JSON column the spec mentioned is
 * achieved via the existing setting_value TEXT column.
 */
@Serializable
data class Wiz3rUserProfile(
    val tax: UserTaxContext,
    val ageBand: AgeBand,
    val homeOwnershipHistory: HomeOwnership,
    val rrspRoomKnowledge: RoomKnowledge,
    /** User-entered RRSP room when [rrspRoomKnowledge] is KNOWN or ROUGH. Null
     *  when UNKNOWN. Drives [studio.StudioOrchestrator.roomFromProfile] →
     *  [ContributionRoom.rrsp] so the projector knows what to cap at. */
    val rrspRoomAmount: Double? = null,
    val tfsaRoomKnowledge: RoomKnowledge,
    val tfsaRoomAmount: Double? = null,
    val savingsTenure: SavingsTenure,
    val goalPriorities: List<GoalCategory> = emptyList(),
    val inflowSources: List<InflowSource> = emptyList(),
    val profileVersion: Int = 1,
    val updatedAt: String,  // ISO-8601
)

// ---------------------------------------------------------------------------
// Tax data
// ---------------------------------------------------------------------------

/**
 * One band in a progressive tax schedule. `upperBound = null` represents the
 * topmost open-ended bracket.
 */
@Serializable
data class TaxBracket(
    val lowerBound: Double,
    val upperBound: Double?,
    val rate: Double,
)

/**
 * Set of brackets effective for one (jurisdiction, taxYear). [verified] is
 * `true` only when the brackets have been hand-validated against the source —
 * the engine refuses to use unverified tables in `STRICT_VERIFY` mode (future).
 */
@Serializable
data class TaxTable(
    val taxYear: Int,
    val jurisdiction: String,         // "FED" or a ProvinceCode name
    val brackets: List<TaxBracket>,
    val sourceUrl: String,
    val effectiveDate: String,        // ISO-8601 date the brackets took effect
    val verified: Boolean,
)

/**
 * CRA annual contribution ceilings. Tracked separately from brackets because
 * they evolve at different cadences and have different sources.
 */
@Serializable
data class CraLimits(
    val taxYear: Int,
    val rrspAnnualMax: Double,
    val tfsaAnnualAddition: Double,
    val fhsaAnnualAddition: Double,
    val fhsaLifetimeCap: Double = 40_000.0,
    val sourceUrl: String,
    val verified: Boolean,
)

// ---------------------------------------------------------------------------
// Inputs for the math engine
// ---------------------------------------------------------------------------

/** All inputs the [VehicleProjector] needs for one projection run. */
data class ProjectionInputs(
    val vehicle: VehicleType,
    val monthlyContribution: Double,
    val monthsToGoal: Int,
    val expectedAnnualReturn: Double,
    val taxContext: UserTaxContext,
    val contributionRoom: ContributionRoom,
    val futureMarginalRate: Double,
    /** Today's date — passed in so tests can pin to a known calendar. */
    val today: LocalDate,
)
